/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║                                                                           ║
 * ║                      📋 WOOORMS - LOGGER INTERFACE 📋                     ║
 * ║                                                                           ║
 * ║  Shared logger interface for consistent logging across client and         ║
 * ║  server. Implementations can vary (Pino for server, console for client).  ║
 * ║                                                                           ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

/**
 * Log levels in order of severity
 */
export type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';

/**
 * Context object for structured logging
 */
export interface LogContext {
  [key: string]: unknown;
}

/**
 * Logger interface for structured logging
 */
export interface Logger {
  /**
   * Log at trace level (most verbose)
   */
  trace(message: string, context?: LogContext): void;

  /**
   * Log at debug level
   */
  debug(message: string, context?: LogContext): void;

  /**
   * Log at info level
   */
  info(message: string, context?: LogContext): void;

  /**
   * Log at warn level
   */
  warn(message: string, context?: LogContext): void;

  /**
   * Log at error level
   */
  error(message: string, context?: LogContext): void;

  /**
   * Log at fatal level (most severe)
   */
  fatal(message: string, context?: LogContext): void;

  /**
   * Create a child logger with additional context
   */
  child(context: LogContext): Logger;

  /**
   * Set the minimum log level
   */
  setLevel(level: LogLevel): void;
}

/**
 * Log level priority for comparison
 */
export const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  trace: 0,
  debug: 1,
  info: 2,
  warn: 3,
  error: 4,
  fatal: 5,
};

/**
 * Check if a log level should be output given the minimum level
 */
export function shouldLog(messageLevel: LogLevel, minLevel: LogLevel): boolean {
  return LOG_LEVEL_PRIORITY[messageLevel] >= LOG_LEVEL_PRIORITY[minLevel];
}

/**
 * Format a log message with context for simple text output
 */
export function formatLogMessage(
  level: LogLevel,
  message: string,
  context?: LogContext,
  timestamp?: boolean
): string {
  const parts: string[] = [];

  if (timestamp !== false) {
    parts.push(`[${new Date().toISOString()}]`);
  }

  parts.push(`[${level.toUpperCase().padEnd(5)}]`);
  parts.push(message);

  if (context && Object.keys(context).length > 0) {
    parts.push(JSON.stringify(context));
  }

  return parts.join(' ');
}

/**
 * Create a structured log object for JSON logging
 */
export function createLogObject(
  level: LogLevel,
  message: string,
  context?: LogContext
): Record<string, unknown> {
  return {
    level,
    message,
    timestamp: new Date().toISOString(),
    ...context,
  };
}

/**
 * No-op logger for testing or disabling logging
 */
export const nullLogger: Logger = {
  trace: () => {},
  debug: () => {},
  info: () => {},
  warn: () => {},
  error: () => {},
  fatal: () => {},
  child: () => nullLogger,
  setLevel: () => {},
};

/**
 * Simple console logger implementation (works in both browser and Node.js)
 * Use this as a fallback or for simple use cases
 */
export function createConsoleLogger(
  initialContext: LogContext = {},
  minLevel: LogLevel = 'info'
): Logger {
  let currentLevel = minLevel;
  const baseContext = { ...initialContext };

  const log = (level: LogLevel, message: string, context?: LogContext): void => {
    if (!shouldLog(level, currentLevel)) return;

    const mergedContext = { ...baseContext, ...context };
    const formattedMessage = formatLogMessage(level, message, mergedContext);

    switch (level) {
      case 'trace':
      case 'debug':
        console.debug(formattedMessage);
        break;
      case 'info':
        console.info(formattedMessage);
        break;
      case 'warn':
        console.warn(formattedMessage);
        break;
      case 'error':
      case 'fatal':
        console.error(formattedMessage);
        break;
    }
  };

  const logger: Logger = {
    trace: (message, context) => log('trace', message, context),
    debug: (message, context) => log('debug', message, context),
    info: (message, context) => log('info', message, context),
    warn: (message, context) => log('warn', message, context),
    error: (message, context) => log('error', message, context),
    fatal: (message, context) => log('fatal', message, context),
    child: (context) => createConsoleLogger({ ...baseContext, ...context }, currentLevel),
    setLevel: (level) => { currentLevel = level; },
  };

  return logger;
}
